import React, { useEffect, useState, useCallback } from "react";
import { BASE_URL } from "../../const";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import { Loader2, Plus, Trash2, Save, ShieldCheck } from "lucide-react";

const PERMISSION_GROUPS = [
  {
    group: "Dropdown Admin",
    permissions: ["dropdown_admin.view", "dropdown_admin.edit"],
  },
  {
    group: "Main Admin",
    permissions: ["main_admin.view", "main_admin.edit"],
  },

{
    group: "Purchase Admin",
    permissions: ["purchase_admin.view", "purchase_admin.edit"],
  },

  {
    group: "Purchase Vendor",
    permissions: ["purchase_vendor.view", "purchase_vendor.edit"],
  },
  {
    group: "Purchase User",
    permissions: ["purchase_user.view", "purchase_user.edit"],
  },


  {
    group: "Inventory",
    permissions: ["inventory.view", "inventory.edit"],
  },
  {
    group: "Quality Check",
    permissions: ["quality_check.view", "quality_check.edit"],
  },
  {
    group: "QC Admin",
    permissions: ["qc_admin.view", "qc_admin.edit"],
  },
  {

    group: "Dashboard",
    permissions: ["dashboard.view"],
  },
  {
    group: "Accounts",
    permissions: ["accounts.view", "accounts.edit"],
  },

  // SKU Management — one card per page
  {
    group: "SKU Management (Landing)",
    permissions: ["sku_management.view"],
  },
  {
    group: "SKU Master",
    permissions: ["sku_master.view", "sku_master.edit"],
  },
  {
    group: "SKU Creation",
    permissions: ["sku_creation.view", "sku_creation.create"],
  },
  {
    group: "Category Master",
    permissions: ["category_master.view", "category_master.edit"],
  },
  {
    group: "NPD Master",
    permissions: ["npd_master.view", "npd_master.edit"],
  },
  {
    group: "Unit Economics",
    permissions: ["unit_economics.view", "unit_economics.edit"],
  },
  {
    group: "Listing Master",
    permissions: ["listing_master.view", "listing_master.edit"],
  },
  {
    group: "Catalogue Master",
    permissions: ["catalouge_master.view", "catalouge_master.edit"],
  },
  {
    group: "I-SKU Explorer",
    permissions: ["isku_explorer.view", "isku_explorer.edit"],
  },

];

const ALL_PERMISSIONS = PERMISSION_GROUPS.flatMap((g) => g.permissions);

const ROLES_URL = `${BASE_URL}/roles/`;

function permissionLabel(perm) {
  const [, action] = perm.split(".");
  return action.charAt(0).toUpperCase() + action.slice(1);
}


function getAuthHeader() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return {};
    if (token.startsWith("Bearer ") || token.startsWith("Token ")) return { Authorization: token };
    return { Authorization: `Token ${token}` };
  } catch (e) { return {}; }
}

export default function UserEdit() {
  const [roles, setRoles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editPerms, setEditPerms] = useState([]);
  const [newRoleName, setNewRoleName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [dirty, setDirty] = useState(false);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(ROLES_URL, {
        headers: { "Content-Type": "application/json", ...getAuthHeader() }
      });
      const data = await res.json();
      setRoles(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const selectRole = (role) => {
    setSelectedId(role.id);
    setEditName(role.name);
    setEditPerms(role.permissions || []);
    setDirty(false);
  };

  const togglePerm = (perm) => {
    setEditPerms((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
    setDirty(true);
  };

  const toggleGroup = (group) => {
    const groupPerms = group.permissions;
    const allSelected = groupPerms.every((p) => editPerms.includes(p));
    if (allSelected) {
      setEditPerms((prev) => prev.filter((p) => !groupPerms.includes(p)));
    } else {
      setEditPerms((prev) => [...new Set([...prev, ...groupPerms])]);
    }
    setDirty(true);
  };

  const toggleAll = () => {
    if (editPerms.length === ALL_PERMISSIONS.length) {
      setEditPerms([]);
    } else {
      setEditPerms([...ALL_PERMISSIONS]);
    }
    setDirty(true);
  };

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const res = await fetch(`${ROLES_URL}${selectedId}/`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify({ name: editName, permissions: editPerms }),
      });
      if (res.ok) {
        const updated = await res.json();
        setRoles((prev) =>
          prev.map((r) => (r.id === selectedId ? updated : r))
        );
        setDirty(false);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    if (!window.confirm(`Delete role "${editName}"?`)) return;
    setDeleting(true);
    try {
      await fetch(`${ROLES_URL}${selectedId}/`, { method: "DELETE", headers: { "Content-Type": "application/json", ...getAuthHeader() } });
      setRoles((prev) => prev.filter((r) => r.id !== selectedId));
      setSelectedId(null);
      setEditName("");
      setEditPerms([]);
    } finally {
      setDeleting(false);
    }
  };

  const handleCreate = async () => {
    const name = newRoleName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const res = await fetch(ROLES_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify({ name, permissions: [] }),
      });
      if (res.ok) {
        const created = await res.json();
        setRoles((prev) => [...prev, created]);
        setNewRoleName("");
        selectRole(created);
      }
    } finally {
      setCreating(false);
    }
  };

  const selected = roles.find((r) => r.id === selectedId);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck className="w-6 h-6 text-indigo-600" />
          <h1 className="text-2xl font-semibold text-gray-800">
            Role & Permission Manager
          </h1>
        </div>

        <div className="flex gap-5 items-start">
          {/* Roles sidebar */}
          <div className="w-64 shrink-0 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Roles
              </p>
            </div>

            {loading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {roles.map((role) => (
                  <li key={role.id}>
                    <button
                      onClick={() => selectRole(role)}
                      className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                        selectedId === role.id
                          ? "bg-indigo-50 text-indigo-700 font-medium"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <span className="block truncate">{role.name}</span>
                      <span className="text-xs text-gray-400 font-normal">
                        {(role.permissions || []).length} permissions
                      </span>
                    </button>
                  </li>
                ))}
                {roles.length === 0 && (
                  <li className="px-4 py-6 text-center text-sm text-gray-400">
                    No roles yet
                  </li>
                )}
              </ul>
            )}

            {/* Add role */}
            <div className="p-3 border-t border-gray-100 bg-gray-50 space-y-2">
              <Input
                placeholder="New role name…"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                className="h-8 text-sm"
              />
              <Button
                size="sm"
                className="w-full gap-1.5"
                onClick={handleCreate}
                disabled={creating || !newRoleName.trim()}
              >
                {creating ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Plus className="w-3.5 h-3.5" />
                )}
                Add Role
              </Button>
            </div>
          </div>

          {/* Permission editor */}
          <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {!selected ? (
              <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                <ShieldCheck className="w-10 h-10 mb-3 opacity-30" />
                <p className="text-sm">Select a role to edit permissions</p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
                  <div className="flex items-center gap-3">
                    <Input
                      value={editName}
                      onChange={(e) => {
                        setEditName(e.target.value);
                        setDirty(true);
                      }}
                      className="h-8 w-52 text-sm font-medium"
                    />
                    {dirty && (
                      <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                        Unsaved
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50"
                      onClick={handleDelete}
                      disabled={deleting}
                    >
                      {deleting ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                      Delete
                    </Button>
                    <Button
                      size="sm"
                      className="gap-1.5"
                      onClick={handleSave}
                      disabled={saving || !dirty}
                    >
                      {saving ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Save className="w-3.5 h-3.5" />
                      )}
                      Save
                    </Button>
                  </div>
                </div>

                {/* Select all */}
                <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={editPerms.length === ALL_PERMISSIONS.length}
                      onCheckedChange={toggleAll}
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Select all permissions
                    </span>
                  </label>
                  <span className="text-xs text-gray-400">
                    {editPerms.length} / {ALL_PERMISSIONS.length} selected
                  </span>
                </div>

                {/* Permission groups */}
                <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {PERMISSION_GROUPS.map((group) => {
                    const allSelected = group.permissions.every((p) =>
                      editPerms.includes(p)
                    );
                    const someSelected =
                      !allSelected &&
                      group.permissions.some((p) => editPerms.includes(p));

                    return (
                      <div
                        key={group.group}
                        className="border border-gray-100 rounded-lg p-4 bg-gray-50/50"
                      >
                        {/* Group header */}
                        <label className="flex items-center gap-2 mb-3 cursor-pointer">
                          <Checkbox
                            checked={allSelected}
                            data-state={someSelected ? "indeterminate" : undefined}
                            onCheckedChange={() => toggleGroup(group)}
                          />
                          <span className="text-sm font-semibold text-gray-700">
                            {group.group}
                          </span>
                        </label>

                        {/* Individual permissions */}
                        <div className="space-y-2 pl-1">
                          {group.permissions.map((perm) => (
                            <label
                              key={perm}
                              className="flex items-center gap-2.5 cursor-pointer"
                            >
                              <Checkbox
                                checked={editPerms.includes(perm)}
                                onCheckedChange={() => togglePerm(perm)}
                              />
                              <div>
                                <p className="text-sm text-gray-700">
                                  {permissionLabel(perm)}
                                </p>
                                <p className="text-xs text-gray-400 font-mono">
                                  {perm}
                                </p>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
